import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  FILES_KIND_FILE,
  FILES_OWNER_SCOPE_COMPANY,
  FILES_SOURCE_IMAGE_EDITOR,
  sanitizeFilesStorageSegment,
} from './companyFiles';
import {
  FILES_IMAGE_EDITOR_FOLDER_ID,
  ensureImageEditorLibraryFolders,
} from './imageEditorDraftsFiles';
import {
  getImageEditorFormatMeta,
  normalizeImageEditorFormat,
} from './imageEditorFormats';
import { buildSaveFilePreview } from './imageEditorGuides';
import { sanitizeAssetIdPart } from './saveEditedAssetVariant';

/**
 * Persist an AI Image Editor canvas to Company Files
 * (Library / Image Editor). Does not write image_editor_drafts (P2 cutover);
 * legacy collection remains for read-fallback until a later release.
 *
 * Storage: companies/{companyId}/files/company/...
 * Firestore: companies/{companyId}/files_nodes/{draftId}
 *
 * @param {{
 *   companyId: string,
 *   blob: Blob,
 *   meta?: {
 *     suffix?: string,
 *     fileStem?: string,
 *     format?: string,
 *     width?: number,
 *     height?: number,
 *   },
 *   sourceAsset?: { id?: string, assetName?: string, type?: string, category?: string, prompt?: string }|null,
 *   uid?: string|null,
 *   draftId?: string|null,
 * }} params
 * @returns {Promise<{
 *   mode: 'draft',
 *   draftId: string,
 *   assetId: string,
 *   downloadUrl: string,
 *   storagePath: string,
 *   format: string,
 * }>}
 */
export async function saveImageEditorDraft(params) {
  const companyId =
    typeof params.companyId === 'string' ? params.companyId.trim() : '';
  const blob = params.blob;
  const uid = params.uid || null;
  const source =
    params.sourceAsset && typeof params.sourceAsset === 'object'
      ? params.sourceAsset
      : {};
  const sourceAssetId =
    typeof source.id === 'string' && source.id.trim()
      ? source.id.trim()
      : typeof source.assetName === 'string' && source.assetName.trim()
        ? source.assetName.trim()
        : 'draft';

  if (!companyId || !blob) {
    throw new Error('companyId and blob are required.');
  }

  await ensureImageEditorLibraryFolders(companyId, uid);

  const format = normalizeImageEditorFormat(params.meta?.format);
  const formatMeta = getImageEditorFormatMeta(format);
  const preview = buildSaveFilePreview(
    (typeof params.meta?.fileStem === 'string' && params.meta.fileStem.trim()) ||
      sourceAssetId,
    format,
  );
  const assetName =
    preview.fileStem ||
    sanitizeAssetIdPart(sourceAssetId, 80) ||
    'draft';

  const existingDraftId =
    typeof params.draftId === 'string' && params.draftId.trim()
      ? params.draftId.trim()
      : '';
  const draftId =
    existingDraftId ||
    `draft_${sanitizeAssetIdPart(assetName, 60)}_${Date.now()}`
      .replace(/_+/g, '_')
      .slice(0, 120);

  const storagePath =
    `companies/${companyId}/files/company/` +
    `${sanitizeFilesStorageSegment(draftId)}_${Date.now()}.${formatMeta.extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: formatMeta.mimeType });
  const downloadUrl = await getDownloadURL(storageRef);

  const nodeRef = doc(db, 'companies', companyId, 'files_nodes', draftId);
  const existingSnap = existingDraftId ? await getDoc(nodeRef) : null;
  const existingData = existingSnap?.exists() ? existingSnap.data() : null;

  /** Preserve linkedAssetId when overwriting a migrated legacy draft. */
  let linkedAssetId =
    existingData && typeof existingData.linkedAssetId === 'string'
      ? existingData.linkedAssetId
      : null;
  if (!linkedAssetId && existingDraftId && !existingSnap?.exists()) {
    // Overwriting a still-legacy id: keep traceability to old draft id.
    linkedAssetId = existingDraftId;
  }

  /** @type {Record<string, unknown>} */
  const payload = {
    kind: FILES_KIND_FILE,
    parentId: FILES_IMAGE_EDITOR_FOLDER_ID,
    name: assetName,
    ownerScope: FILES_OWNER_SCOPE_COMPANY,
    ownerUid: null,
    storagePath,
    downloadUrl,
    mimeType: formatMeta.mimeType,
    bytes: typeof blob.size === 'number' ? blob.size : null,
    source: FILES_SOURCE_IMAGE_EDITOR,
    linkedProjectId: null,
    linkedAssetId,
    format,
    type:
      typeof source.type === 'string' && source.type.trim()
        ? source.type.trim()
        : 'sprite',
    category:
      typeof source.category === 'string' && source.category.trim()
        ? source.category.trim()
        : 'prop',
    promptUsed:
      typeof source.prompt === 'string' ? source.prompt.trim() : '',
    updatedAt: serverTimestamp(),
    updatedByUid: uid,
  };

  if (typeof params.meta?.width === 'number' && params.meta.width > 0) {
    payload.width = Math.round(params.meta.width);
  }
  if (typeof params.meta?.height === 'number' && params.meta.height > 0) {
    payload.height = Math.round(params.meta.height);
  }

  if (existingSnap?.exists()) {
    await setDoc(
      nodeRef,
      {
        ...payload,
      },
      { merge: true },
    );
  } else {
    await setDoc(nodeRef, {
      ...payload,
      createdAt: serverTimestamp(),
      createdByUid: uid,
    });
  }

  return {
    mode: 'draft',
    draftId,
    assetId: assetName,
    downloadUrl,
    storagePath,
    format,
  };
}
